import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FiGrid, FiPlay, FiCheckSquare, FiTerminal,
  FiMessageSquare, FiClipboard, FiAward,
  FiUser, FiChevronLeft,
  FiChevronRight, FiShield, FiFileText,
} from 'react-icons/fi';
import './Navbar.css';

/* ── Nav item definitions ───────────────────────────────── */
const NAV_MAIN = [
  { label: 'Dashboard',      path: '/',            icon: FiGrid,          exact: true  },
  { label: 'My Learning',    path: '/mylearning',  icon: FiPlay                        },
  { label: 'Tasks',          path: '/tasks',       icon: FiCheckSquare                 },
  { label: 'Simulations',    path: '/simulations', icon: FiTerminal                    },
  { label: 'Interview Prep', path: '/interview',    icon: FiMessageSquare, cohortOnly: true },
  { label: 'Mock Exams',     path: '/mockexam',    icon: FiClipboard,    cohortOnly: true },
  { label: 'Resume Prep',    path: '/resume-prep', icon: FiFileText,     cohortOnly: true },
];

const NAV_ACCOUNT = [
  { label: 'Achievements', path: '/achievements-anchor', icon: FiAward  },
  { label: 'Profile',      path: '/profile',             icon: FiUser   },
];

/* ── Helper: is route active ────────────────────────────── */
function isActive(item, pathname) {
  if (item.exact) return pathname === item.path;
  if (item.path === '/') return false;
  return pathname === item.path || pathname.startsWith(item.path + '/');
}

/* ── Nav link item ──────────────────────────────────────── */
function NavItem({ item, collapsed, onClick }) {
  const location = useLocation();
  const navigate  = useNavigate();
  const active   = isActive(item, location.pathname);
  const Icon     = item.icon;

  const handleClick = (e) => {
    if (item.path === '/achievements-anchor') {
      e.preventDefault();
      if (location.pathname === '/') {
        document.querySelector('.badges-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        navigate('/', { state: { scrollTo: 'badges-section' } });
      }
      onClick?.();
      return;
    }
    onClick?.();
  };

  return (
    <Link
      to={item.path === '/achievements-anchor' ? '/' : item.path}
      className={`nav-item ${active ? 'nav-item--active' : ''} ${collapsed ? 'nav-item--collapsed' : ''}`}
      onClick={handleClick}
      title={collapsed ? item.label : undefined}
    >
      <span className="nav-item-icon"><Icon size={18} /></span>
      {/* Always render both — CSS hides the appropriate one */}
      <span className="nav-item-label">{item.label}</span>
      <span className="nav-item-tooltip" aria-hidden="true">{item.label}</span>
    </Link>
  );
}

/* ── Main Navbar component ──────────────────────────────── */
export default function Navbar({ user, signOut, userName, isAdmin, isCohortAssigned, mobileOpen, onMobileClose }) {
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('sidebar-collapsed') === 'true';
  });

  useEffect(() => {
    document.documentElement.setAttribute(
      'data-sidebar',
      collapsed ? 'collapsed' : 'expanded'
    );
  }, [collapsed]);

  useEffect(() => {
    document.documentElement.setAttribute(
      'data-sidebar',
      collapsed ? 'collapsed' : 'expanded'
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('sidebar-collapsed', String(next));
  };

  const closeMobile = () => onMobileClose?.();

  const initials = userName
    ? userName.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <>
      {/* Mobile backdrop — always in DOM, CSS controls visibility */}
      <div
        className={`sidebar-backdrop${mobileOpen ? ' sidebar-backdrop--visible' : ''}`}
        onClick={closeMobile}
        aria-hidden="true"
      />

      <aside
        className={[
          'sidebar',
          collapsed  ? 'sidebar--collapsed'   : '',
          mobileOpen ? 'sidebar--mobile-open' : '',
        ].filter(Boolean).join(' ')}
      >
        {/* ── Logo ── */}
        <div className="sidebar-logo">
          <Link to="/" className="sidebar-logo-link" onClick={closeMobile} title="Go to Dashboard">
            <div className="sidebar-logo-mark">
              <img src="/logo.png" alt="TeraSkill" className="sidebar-logo-img" />
            </div>
            {/* Always in DOM — CSS hides when collapsed */}
            <div className="sidebar-logo-text">
              <span className="sidebar-logo-name">
                Tera<span className="sidebar-logo-accent">Skill</span>
              </span>
              <span className="sidebar-logo-sub">Academy</span>
            </div>
          </Link>
          <button
            className="sidebar-collapse-btn"
            onClick={toggleCollapse}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <FiChevronRight size={14} /> : <FiChevronLeft size={14} />}
          </button>
        </div>

        {/* ── Main navigation ── */}
        <nav className="sidebar-nav">
          {/* Always in DOM — CSS hides when collapsed */}
          <span className="sidebar-nav-label">Main Menu</span>
          {NAV_MAIN
            .filter(item => !item.cohortOnly || isCohortAssigned)
            .map(item => (
              <NavItem key={item.path} item={item} collapsed={collapsed} onClick={closeMobile} />
            ))}
        </nav>

        {/* ── Divider ── */}
        <div className="sidebar-divider" />

        {/* ── Account navigation ── */}
        <nav className="sidebar-nav">
          {/* Always in DOM — CSS hides when collapsed */}
          <span className="sidebar-nav-label">Account</span>
          {NAV_ACCOUNT.map(item => (
            <NavItem key={item.path} item={item} collapsed={collapsed} onClick={closeMobile} />
          ))}
          {isAdmin && (
            <NavItem
              item={{ label: 'Admin', path: '/admin', icon: FiShield }}
              collapsed={collapsed}
              onClick={closeMobile}
            />
          )}
        </nav>

        {/* ── Spacer ── */}
        <div className="sidebar-spacer" />

        {/* ── User footer ── */}
        <div className={`sidebar-user ${collapsed ? 'sidebar-user--collapsed' : ''}`}>
          <div className={`sidebar-user-avatar ${collapsed ? 'sidebar-user-avatar--collapsed' : ''}`}>
            {initials}
          </div>
          {/* Always in DOM — CSS hides when collapsed */}
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">{userName}</span>
            <span className="sidebar-user-role">DevOps Learner</span>
          </div>
        </div>
      </aside>
    </>
  );
}
